import { createFileRoute } from "@tanstack/react-router";
import React from "react";
import { Archive, ArrowLeft, Clock, Info, MapPin, Users } from "lucide-react";
import { PageSkeleton } from "@/components/shared/Skeletons";
import { Link } from "@tanstack/react-router";
import PageHeader from "@/components/shared/PageHeader";
import SectionCard from "@/components/shared/SectionCard";
import StatusChip from "@/components/shared/StatusChip";
import EmptyState from "@/components/shared/EmptyState";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useArchiveSlot, usePartnerSlot } from "@/hooks/usePartnerData";

export const Route = createFileRoute("/partner/slots/$id")({ component: SlotDetail });

function SlotDetail() {
  const { id } = Route.useParams();
  const { data: slot, isLoading } = usePartnerSlot(id);
  const archiveSlot = useArchiveSlot();

  if (isLoading) {
    return <PageSkeleton />;
  }

  if (!slot) {
    return (
      <div className="space-y-4">
        <EmptyState
          title="Slot not found"
          description="This placement slot may have been removed or archived."
        />
        <div className="flex justify-center">
          <Button variant="outline" asChild>
            <Link to="/partner/slots">Back to slots</Link>
          </Button>
        </div>
      </div>
    );
  }

  const capacity = slot.max_students || slot.maxStudents || 0;
  const status = (slot.status || "published").replace("published", "open");
  const skills = (slot.requirements || "")
    .split(",")
    .map((item: string) => item.trim())
    .filter(Boolean);

  return (
    <div className="space-y-6">
      <PageHeader
        title={slot.title}
        subtitle="Placement slot detail"
        breadcrumbs={[{ label: "Slots", path: "/partner/slots" }, { label: slot.title }]}
        action={
          <>
            <Button variant="outline" asChild>
              <Link to="/partner/slots">
                <ArrowLeft />
                Back to slots
              </Link>
            </Button>
            <Button
              variant="destructive-outline"
              onClick={() => archiveSlot.mutate(id)}
              loading={archiveSlot.isPending}
            >
              {!archiveSlot.isPending && <Archive />}
              {archiveSlot.isPending ? "Updating..." : "Archive slot"}
            </Button>
          </>
        }
      />

      <Alert variant="info">
        <Info />
        <AlertDescription className="text-foreground">
          Use this slot view to confirm capacity, requirements, location, and readiness for
          verified student applications.
        </AlertDescription>
      </Alert>

      <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <SectionCard title="Slot overview">
          <div className="space-y-5">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <p className="eyebrow mb-1">Slot status</p>
                <h2 className="text-title text-foreground">{slot.title}</h2>
                <p className="mt-1 text-sm leading-6 text-body">
                  {slot.description || "No detailed description has been provided yet."}
                </p>
              </div>
              <StatusChip status={status} className="self-start" />
            </div>

            <div className="grid gap-3 md:grid-cols-3">
              <FactCard icon={<MapPin />} label="Location" value={slot.location || "Not specified"} />
              <FactCard icon={<Clock />} label="Duration" value={slot.duration || "Flexible"} />
              <FactCard icon={<Users />} label="Capacity" value={`${capacity} students`} />
            </div>

            <div className="border-t border-border pt-5">
              <h3 className="mb-2 text-sm font-medium text-foreground">Required skills</h3>
              <div className="flex flex-wrap gap-1.5">
                {skills.length > 0 ? (
                  skills.map((skill: string) => (
                    <Badge key={skill} variant="secondary">
                      {skill}
                    </Badge>
                  ))
                ) : (
                  <Badge variant="secondary">General professional readiness</Badge>
                )}
              </div>
            </div>
          </div>
        </SectionCard>

        <SectionCard title="Posting guide">
          <ol className="space-y-3">
            {[
              "Use clear role titles and realistic learning outcomes.",
              "State whether the work is on-site, remote, or hybrid in the description.",
              "List the core skills you truly expect from applicants.",
              "Keep the opportunity aligned with verified student readiness.",
            ].map((line, i) => (
              <li key={line} className="flex gap-3 text-sm leading-6 text-body">
                <span className="font-mono text-caption leading-6 text-muted-foreground tabular-nums">
                  {String(i + 1).padStart(2, "0")}
                </span>
                {line}
              </li>
            ))}
          </ol>
        </SectionCard>
      </div>
    </div>
  );
}

function FactCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-center gap-3 rounded-sm border border-border bg-background p-3">
      <span
        className="flex size-8 shrink-0 items-center justify-center rounded-sm border border-border bg-card text-body [&_svg]:size-4"
        aria-hidden
      >
        {icon}
      </span>
      <div className="min-w-0">
        <p className="eyebrow">{label}</p>
        <p className="truncate text-sm font-medium text-foreground">{value}</p>
      </div>
    </div>
  );
}
